const isDev = import.meta.env.DEV;

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const minLevel: LogLevel = isDev ? 'debug' : 'warn';

function shouldLog(level: LogLevel): boolean {
  return LEVEL_PRIORITY[level] >= LEVEL_PRIORITY[minLevel];
}

function formatTag(tag: string): string {
  return `[${tag}]`;
}

function formatMessage(args: unknown[]): string {
  return args
    .map((a) =>
      a instanceof Error ? a.message : typeof a === 'string' ? a : JSON.stringify(a)
    )
    .join(' ');
}

// --- Remote log upload (sends warn/error to server) ---

interface PendingLogEntry {
  timestamp: string;
  level: string;
  target: string;
  message: string;
}

const pendingLogs: PendingLogEntry[] = [];
let uploadTimer: ReturnType<typeof setInterval> | null = null;

function queueForUpload(level: LogLevel, tag: string, args: unknown[]) {
  if (level !== 'warn' && level !== 'error') return;
  pendingLogs.push({
    timestamp: new Date().toISOString(),
    level: level.toUpperCase(),
    target: tag,
    message: formatMessage(args),
  });
  // Cap buffer to avoid memory growth
  if (pendingLogs.length > 500) pendingLogs.splice(0, pendingLogs.length - 500);
}

/** Convert a WebSocket URL (ws:// or wss://) to its HTTP equivalent. */
function toHttpUrl(url: string): string {
  if (url.startsWith('wss://')) return 'https://' + url.slice(6);
  if (url.startsWith('ws://')) return 'http://' + url.slice(5);
  return url;
}

async function flushLogs() {
  if (pendingLogs.length === 0) return;

  // Get auth token and server URL
  try {
    const { useAuthStore } = await import('../stores/authStore');
    const { useSettingsStore } = await import('../stores/settingsStore');
    const auth = useAuthStore.getState();
    const settings = useSettingsStore.getState();

    if (!auth.token || !settings.server.url) return;

    // The server.url is a WebSocket URL (wss://). Convert to HTTPS for
    // the REST log ingestion endpoint — fetch() doesn't support wss://.
    const httpBase = toHttpUrl(settings.server.url);

    const entries = pendingLogs.splice(0, 100); // Take up to 100

    const resp = await fetch(`${httpBase}/logs/ingest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${auth.token}`,
      },
      body: JSON.stringify({
        entries,
        machineName: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 50) : '',
        appVersion: '', // Filled by the app if available
        platform: typeof navigator !== 'undefined' ? navigator.platform : '',
      }),
    });

    if (!resp.ok) {
      // Put entries back on failure
      pendingLogs.unshift(...entries);
    }
  } catch {
    // Silently fail — don't let log upload errors cause more errors
  }
}

// Start periodic upload (every 60 seconds)
if (typeof window !== 'undefined') {
  uploadTimer = setInterval(flushLogs, 60_000);
  // Also flush on page unload
  window.addEventListener('beforeunload', () => {
    if (uploadTimer) clearInterval(uploadTimer);
    // Best-effort sync flush via sendBeacon is not suitable for JSON POST,
    // so we just let the next session pick up where we left off
  });
}

// --- Public logger API (unchanged) ---

export const logger = {
  debug(tag: string, ...args: unknown[]) {
    if (shouldLog('debug')) console.log(formatTag(tag), ...args);
  },
  info(tag: string, ...args: unknown[]) {
    if (shouldLog('info')) console.info(formatTag(tag), ...args);
  },
  warn(tag: string, ...args: unknown[]) {
    if (shouldLog('warn')) console.warn(formatTag(tag), ...args);
    queueForUpload('warn', tag, args);
  },
  error(tag: string, ...args: unknown[]) {
    if (shouldLog('error')) console.error(formatTag(tag), ...args);
    queueForUpload('error', tag, args);
  },
};

// --- Global error handlers ---
// Capture uncaught errors and unhandled promise rejections.
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    logger.error('UncaughtError', event.message, event.filename ? `${event.filename}:${event.lineno}` : '');
  });
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('UnhandledRejection', String(event.reason));
  });
}
